import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { supabase } from '../lib/supabase';

export interface Admin {
  id: number;
  created_at: string;
  auth0ID: string;
  email: string;
  firstname: string;
  lastname: string;
  mobilenumber: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
}

export function useAdminStatus() {
  const { user, isAuthenticated } = useAuth0();
  const [adminStatus, setAdminStatus] = useState<'LOADING' | 'NOT_FOUND' | 'PENDING' | 'APPROVED' | 'DENIED'>('LOADING');
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [pendingAdmins, setPendingAdmins] = useState<Admin[]>([]);
  const [allAdmins, setAllAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = async () => {
    if (!user?.sub || !isAuthenticated) {
      setAdminStatus('NOT_FOUND');
      setLoading(false);
      return;
    }

    try {
      const { data: admin, error } = await supabase
        .from('admins')
        .select('*')
        .eq('auth0ID', user.sub)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking admin status:', error);
        setAdminStatus('NOT_FOUND');
      } else if (admin) {
        setAdmin(admin);
        setAdminStatus(admin.status);
      } else {
        setAdminStatus('NOT_FOUND');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setAdminStatus('NOT_FOUND');
    } finally {
      setLoading(false);
    }
  };

  const registerAdmin = async (adminData: Omit<Admin, 'id' | 'created_at' | 'status'>) => {
    try {
      const { data: newAdmin, error } = await supabase
        .from('admins')
        .insert({
          ...adminData,
          status: 'PENDING'
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      setAdmin(newAdmin);
      setAdminStatus('PENDING');
      return newAdmin;
    } catch (error) {
      console.error('Error registering admin:', error);
      throw error;
    }
  };

  const fetchPendingAdmins = async () => {
    if (adminStatus !== 'APPROVED') {
      console.log('Not fetching pending admins - status:', adminStatus);
      return;
    }

    console.log('Fetching pending admins...');
    try {
      const { data: pendingAdmins, error } = await supabase
        .from('admins')
        .select('*')
        .eq('status', 'PENDING')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching pending admins:', error);
        return;
      }

      console.log('Fetched pending admins:', pendingAdmins);
      setPendingAdmins(pendingAdmins || []);
    } catch (error) {
      console.error('Error fetching pending admins:', error);
    }
  };

  const fetchAllAdmins = async () => {
    if (adminStatus !== 'APPROVED') {
      console.log('Not fetching all admins - status:', adminStatus);
      return;
    }

    console.log('Fetching all admins...');
    try {
      const { data: admins, error } = await supabase
        .from('admins')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching all admins:', error);
        return;
      }

      console.log('Fetched all admins:', admins);
      setAllAdmins(admins || []);
    } catch (error) {
      console.error('Error fetching all admins:', error);
    }
  };

  const updateAdminStatus = async (adminId: number, status: 'APPROVED' | 'DENIED') => {
    try {
      const { data: updatedAdmin, error } = await supabase
        .from('admins')
        .update({ status })
        .eq('id', adminId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      // Refresh both pending and all admins lists
      await fetchPendingAdmins();
      await fetchAllAdmins();
      
      return updatedAdmin;
    } catch (error) {
      console.error('Error updating admin status:', error);
      throw error;
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (adminStatus === 'APPROVED') {
      fetchPendingAdmins();
      fetchAllAdmins();
    }
  }, [adminStatus]);

  return {
    adminStatus,
    admin,
    pendingAdmins,
    allAdmins,
    loading,
    registerAdmin,
    updateAdminStatus,
    refreshStatus: checkAdminStatus,
    refreshPendingAdmins: fetchPendingAdmins,
    refreshAllAdmins: fetchAllAdmins,
  };
}
